import { useEffect, useState, type RefObject } from "react";

type UseInViewOptions = {
  once?: boolean;
  margin?: string;
  threshold?: number | number[];
};

export function useInView(
  ref: RefObject<Element | null>,
  { once = true, margin = "0px 0px -6% 0px", threshold = 0 }: UseInViewOptions = {},
): boolean {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }

    // Synchronously check if the element is already in the viewport using
    // getBoundingClientRect. This avoids the async gap before the IO's first
    // intersection callback — during which RevealLines text sits at
    // translateY(110%) (invisible) on slow devices.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setInView(true);
      if (once) return;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (once) io.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { rootMargin: margin, threshold },
    );

    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [once, margin, JSON.stringify(threshold)]);

  return inView;
}
